import React, { useState } from 'react';
import { FormComponent, UiComponent } from 'components';
import useModal from 'hooks/useModal';
import { toast } from 'react-toastify';
import {
  APPLICATION_TICKET_SELECT,
  ApplicationBaseType,
  getApplicationTypeLabel,
} from 'types';

export interface TicketDraft {
  base: ApplicationBaseType;
  value: number;
  title: string;
}

interface Props {
  /** 지금 목록에 있는 유형들. 같은 유형이 이미 있으면 티켓 명이 필요해진다. */
  existingBases: ApplicationBaseType[];
  onSubmit: (draft: TicketDraft) => void;
}

/**
 * 다중 티켓 유형 추가 모달.
 *
 * 유형(의미)은 여기서 한 번만 정하고 추가 뒤에는 바꿀 수 없다.
 * 목록에서 인라인으로 유형을 바꾸게 두면 저장 키가 통째로 밀리면서
 * 다른 행의 유형까지 뒤바뀐다. 티켓 명과 인원은 추가 뒤에도 계속 수정할 수 있다.
 *
 * 저장 키(`GENERAL`, `GENERAL_2` …)는 여기서 만들지 않는다.
 * 목록 순서에서 한 번에 계산하므로 이 모달은 의미 축만 고른다.
 */
function TicketFormModal({ existingBases, onSubmit }: Props) {
  const { handleClose } = useModal();

  const [base, setBase] = useState<ApplicationBaseType>('GENERAL');
  const [title, setTitle] = useState('');
  const [count, setCount] = useState('');

  // 같은 의미의 유형이 이미 있으면 티켓 명 없이는 신청 화면에서 구분할 수 없다.
  // (운전이 2개인데 둘 다 '운전' 으로 보이면 사용자가 무엇을 고를지 알 수 없다)
  const isDuplicatedBase = existingBases.includes(base);

  const handleSubmit = () => {
    const parsed = Number(count);

    if (!count || isNaN(parsed) || parsed <= 0) {
      toast.error('인원은 1 이상이어야 합니다.');
      return;
    }

    if (isDuplicatedBase && !title.trim()) {
      toast.error('같은 유형이 이미 있어 티켓 명이 필요합니다.');
      return;
    }

    onSubmit({ base, value: parsed, title: title.trim() });
    handleClose();
  };

  return (
    <div css={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <label css={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span css={{ width: '60px' }}>유형</span>
        <FormComponent.Select
          css={{ width: '160px' }}
          options={APPLICATION_TICKET_SELECT}
          value={base}
          handleChange={setBase}
        />
      </label>

      <label css={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span css={{ width: '60px' }}>티켓 명</span>
        <FormComponent.Input
          css={{ width: '240px' }}
          placeholder={isDuplicatedBase ? '필수' : '비우면 기본 이름으로 노출'}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </label>

      <label css={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span css={{ width: '60px' }}>인원</span>
        <FormComponent.Input
          css={{ width: '80px' }}
          type="number"
          min={1}
          value={count}
          onChange={(e) => setCount(e.target.value)}
        />
      </label>

      <p css={{ margin: 0, fontSize: '13px', color: '#8d8d8d' }}>
        {`신청 화면에는 "${title.trim() || getApplicationTypeLabel(base)}" 으로 노출됩니다.`}
        {isDuplicatedBase &&
          ' 같은 유형이 이미 있어 티켓 명을 반드시 입력해야 합니다.'}
      </p>

      <div css={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <UiComponent.Button
          variant="border"
          color="primary"
          onClick={handleClose}
        >
          취소
        </UiComponent.Button>
        <UiComponent.Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
        >
          추가
        </UiComponent.Button>
      </div>
    </div>
  );
}

export default TicketFormModal;
